import { CURRENCY } from "@/data/numbers";
import { formatPrice } from "@/lib/numbers";
import { type Product } from "@repo/schemas";
import { Truck } from "lucide-react";
import Image from "next/image";
import { useState } from "react";
import { Button } from "../base/button";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "../base/carousel";
import { SlideShow } from "./SlideShow";

export function ProductDetails({
  product,
  onAddToCart,
}: {
  product: Product;
  onAddToCart: () => void;
}) {
  const [isSlideShowVisible, setIsSlideShowVisible] = useState(false);
  const productImages = product.images.filter((pimg) => pimg.type === "big");

  return (
    <>
      <article className="grid gap-x-8 gap-y-4 md:grid-cols-2 md:gap-x-16 2xl:gap-x-24">
        <section>
          <Carousel className="w-full" opts={{ loop: true }}>
            <CarouselContent>
              {productImages.map((productImage) => (
                <CarouselItem key={productImage.src}>
                  <Button
                    className="relative aspect-square w-full"
                    onClick={() => {
                      setIsSlideShowVisible(true);
                    }}
                  >
                    <Image
                      src={productImage.src}
                      alt={product.title}
                      fill
                      sizes="(max-width: 768px) 900vw, 300px"
                    />
                  </Button>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="left-0 opacity-40 hover:opacity-100" />
            <CarouselNext className="right-0 opacity-40 hover:opacity-100" />
          </Carousel>
        </section>
        <section className="flex flex-col gap-4">
          <header>
            <p className="font-didone mb-2 text-3xl">{product.brand}</p>
            <h1 className="font-bold">{product.title}</h1>
          </header>

          <p>{product.description}</p>
          <p className="text-xl font-bold">
            {formatPrice(product.price, CURRENCY)}
          </p>
          <Button
            variant="default"
            size="lg"
            onClick={() => {
              onAddToCart();
            }}
          >
            ADD TO CART
          </Button>

          <p className="mt-4 flex gap-4 text-green-700">
            <Truck />
            In stock, ships on {generateShippingDate()}
          </p>

          <p className="italic">
            The prices of the items sold include VAT. Depending on your delivery
            address, the VAT may vary at checkout. For more information, please
            see our policy page.
          </p>
        </section>
      </article>
      {isSlideShowVisible && (
        <SlideShow
          images={productImages.map((el) => el.src)}
          onClose={() => {
            setIsSlideShowVisible(false);
          }}
        />
      )}
    </>
  );
}

function generateShippingDate() {
  const date = new Date();
  date.setDate(date.getDate() + 7);

  const formattedDate = date.toLocaleDateString("en-GB");
  return formattedDate;
}
